import { useContext, useEffect, useState, useCallback } from 'react';
import { UserContext } from '@services/Session';
import { useIsFeatureEnabled } from '@helpers/flagUtils';
import { ViewerEntitlementStatus } from '@components/Paywall/paywall.types';
import {
  isMonetizedSport,
  isLoggedInUserEntitled,
  isAdminOrCoach,
  isPlayerEntitled,
} from '@helpers/utils/monetizationUtils';

interface EntitlementStatus {
  entitlement: ViewerEntitlementStatus;
  isEntitled: boolean;
}

const useEntitlements = ({
  teamId,
  playerId,
  sport,
}: {
  teamId?: string;
  playerId?: string;
  sport?: string;
}) => {
  const userStore = useContext(UserContext);
  const isLoggedIn = !!userStore.authUser;
  const monetizationEnabled = useIsFeatureEnabled('vb_monetization', userStore);
  const [loading, setLoading] = useState(true);
  //const [error, setError] = useState<string | null>(null);
  const [canEditMonetizedFields, setCanEditMonetizedFields] =
    useState<boolean>(false);
  const [entitlementStatus, setEntitlementStatus] =
    useState<ViewerEntitlementStatus | null>(null);

  const checkPlayerEntitlement = useCallback(
    async (
      id: string,
      userIsEntitledBySubscription: boolean = false
    ): Promise<EntitlementStatus> => {
      const playerEntitlement = await isPlayerEntitled(id);
      const isMyPlayer = userStore.userPlayers.map((p) => p._id).includes(id);
      if (
        ((isMyPlayer && userIsEntitledBySubscription) ||
          userIsEntitledBySubscription) &&
        playerEntitlement !== ViewerEntitlementStatus.ENTITLED_BUT_PRIVATE
      ) {
        return {
          entitlement: ViewerEntitlementStatus.ENTITLED,
          isEntitled: true,
        };
      }
      // general player entitlement check for not subscribed users
      const entitled = playerEntitlement === ViewerEntitlementStatus.ENTITLED;
      return { entitlement: playerEntitlement, isEntitled: entitled };
    },
    [userStore.userPlayers] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const checkTeamEntitlement = useCallback(
    async (
      id: string,
      entitledBySubscrption: boolean
    ): Promise<EntitlementStatus> => {
      let teamEntitlement = ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_IN;
      const adminOrCoach = await isAdminOrCoach(id);
      if (adminOrCoach || entitledBySubscrption) {
        teamEntitlement = ViewerEntitlementStatus.ENTITLED;
      }
      return {
        entitlement: teamEntitlement,
        isEntitled: teamEntitlement === ViewerEntitlementStatus.ENTITLED,
      };
    },
    []
  );

  useEffect(() => {
    const checkEntitlement = async () => {
      let status: ViewerEntitlementStatus =
        ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT;

      let canEditPlayerFields: boolean = false;

      let player: EntitlementStatus = {
        entitlement: ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT,
        isEntitled: false,
      };

      if (!isMonetizedSport(sport, monetizationEnabled)) {
        setCanEditMonetizedFields(true);
        setEntitlementStatus(ViewerEntitlementStatus.ENTITLED);
        setLoading(false);
        return;
      }

      // Not Logged In:
      if (!isLoggedIn) {
        player =
          (playerId && (await checkPlayerEntitlement(playerId))) || player;
        if (player && player?.isEntitled) {
          status = player.entitlement;
        } else {
          status = ViewerEntitlementStatus.NOT_ENTITLED_LOGGED_OUT;
        }
      } else {
        const userIsEntitledBySubscription = await isLoggedInUserEntitled();

        if (teamId) {
          const team = await checkTeamEntitlement(
            teamId,
            userIsEntitledBySubscription
          );
          status = team.entitlement;
        }

        if (playerId) {
          player = await checkPlayerEntitlement(
            playerId,
            userIsEntitledBySubscription
          );
          canEditPlayerFields = player.isEntitled;
          status = player.entitlement;
        }
      }
      setEntitlementStatus(status);
      setCanEditMonetizedFields(canEditPlayerFields);
      setLoading(false);
    };
    (teamId || playerId) && checkEntitlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, playerId, monetizationEnabled, sport]);

  return { canEditMonetizedFields, entitlementStatus, loading, error: null };
};

export default useEntitlements;
